import type { MechanicDefinition, MechanicEvent, MechanicContext, EventReaction } from "./types";

export const worldMap: MechanicDefinition = {
  id: "world_map",
  name: "World Map (Beta)",
  description: "Interactive world map with location placement, nested drill-down maps, travel time calculation, and fog of war. Locations from the Entities panel can be placed on layered maps.",
  icon: "Map",
  category: "systems",
  aiHints: {
    auditPasses: 1,
    purpose: "Shows an interactive map to players with placed location nodes, travel time, and optional fog of war.",
    whenToUse: "Worlds with a visual map where location relationships and travel distances matter.",
    synergies: ["region_movement", "territory", "time_passage"],
    conflicts: [],
    prerequisites: ["Location entities and at least one map image uploaded"],
    tags: ["map", "visual", "locations", "travel", "fog-of-war"],
    complexity: 3,
    exampleConfig: { fog_of_war: false, show_travel_time: true },
    stateVariables: ["_discovered_locations", "_current_map_id"],
  },
  configSchema: [
    {
      key: "enable_travel_time",
      label: "Enable Travel Time",
      type: "boolean",
      default: true,
      description: "Calculate travel time between locations using per-map scale settings and stored route distances.",
    },
    {
      key: "fog_of_war",
      label: "Fog of War",
      type: "boolean",
      default: false,
      description: "Hide unvisited locations from the player's map view. Locations are revealed when visited or via reveal_map_location tool. The player's starting location is always visible.",
    },
  ],

  systemPromptSection(config) {
    let section = `## World Map\nThe world has an interactive map with placed locations. `;
    section += `Each map has its own scale configuration (distance unit, total distance, speed modifier) stored in its renderer config. `;

    if (config.enable_travel_time) {
      section += `Travel between locations takes time. Route distances are stored on each connection — use the stored "distance" and "travel_time_hours" values when available instead of inventing numbers. `;
      section += `If no stored distance exists, estimate based on the map's scale (totalDistance / unit from the map's scale config). `;
      section += `Apply small random variance (±10-20%) to travel times based on terrain, weather, and encounters. `;
      section += `Use the travel_to_location tool when the player moves between map locations. `;
      section += `If the time_passage mechanic is active, advance the in-game clock by the travel duration. `;
    }

    if (config.fog_of_war) {
      section += `\nFog of war is enabled — the player can only see locations they have visited or that have been revealed. `;
      section += `Use reveal_map_location to make new locations visible when the player learns about them (through NPC hints, finding maps, scouting, etc.). `;
      section += `The player's current location and starting location are always visible. `;
      section += `When an NPC mentions a place, consider revealing it if the information is specific enough. `;
    }

    section += `\nUse describe_map_location when the player examines a location on the map.`;
    section += `\nUse query_nearby_locations to check what's near the player and enrich narration with spatial awareness.`;
    section += `\nUse get_travel_distance to get exact distance/route info between locations for realistic travel narration.`;

    return section;
  },

  tools: [
    {
      type: "function",
      function: {
        name: "travel_to_location",
        description: "Move the player to a new map location. Calculates travel time based on distance. Updates current_location and optionally advances time.",
        parameters: {
          type: "object",
          properties: {
            destination: {
              type: "string",
              description: "Name or ID of the destination location.",
            },
            travel_method: {
              type: "string",
              description: "How the player is traveling (on foot, horseback, ship, teleport, etc.).",
            },
            speed_modifier: {
              type: "number",
              description: "Multiplier for travel speed (1.0 = normal, 0.5 = half speed, 2.0 = double).",
            },
          },
          required: ["destination"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "reveal_map_location",
        description: "Make a hidden location visible on the player's map (fog of war). Use when the player learns about a new place.",
        parameters: {
          type: "object",
          properties: {
            location_name: {
              type: "string",
              description: "Name of the location to reveal on the map.",
            },
            reveal_reason: {
              type: "string",
              description: "Why the location is being revealed (heard rumor, given map, etc.).",
            },
          },
          required: ["location_name"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "describe_map_location",
        description: "Provide the player with information about a map location based on their current knowledge.",
        parameters: {
          type: "object",
          properties: {
            location_name: {
              type: "string",
              description: "Name of the location to describe.",
            },
            detail_level: {
              type: "string",
              enum: ["brief", "detailed"],
              description: "How much detail to provide.",
            },
          },
          required: ["location_name"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "discover_route",
        description: "Create a new travel route between locations discovered during gameplay. Use when the narrative justifies a new connection (e.g., player finds a hidden path, builds a bridge, opens a portal). If entity_name references something new, a new entity will be auto-created.",
        parameters: {
          type: "object",
          properties: {
            from: { type: "string", description: "Name of the origin location." },
            to: { type: "string", description: "Name of the destination location." },
            style: {
              type: "string",
              enum: ["road", "river", "trail", "border", "sea_route", "shuttle", "portal", "custom"],
              description: "Type of route.",
            },
            entity_name: { type: "string", description: "Name of the entity associated with this route (e.g., 'Ancient Railway', 'Smuggler Tunnel')." },
            description: { type: "string", description: "Short narrative description of the route." },
            bidirectional: { type: "boolean", description: "Whether the route works both ways (default: true)." },
          },
          required: ["from", "to", "style", "description"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "update_map_location_display",
        description: "Change the visual appearance of a map location — icon, colors, subtitle, outline. Use for faction territory changes, conquest, destruction, corruption spread, or any event that visually transforms how a location appears on the map. IMPORTANT: Every such change must be narratively justified and the result of meaningful story events, not arbitrary.",
        parameters: {
          type: "object",
          properties: {
            location_name: {
              type: "string",
              description: "Name of the location to visually update.",
            },
            icon_name: {
              type: "string",
              description: "New icon (map-pin, castle, swords, tent, trees, mountain, anchor, church, store, home, crown, skull, gem, flag, shield, warehouse, flame, snowflake, compass, star, circle-dot, triangle).",
            },
            icon_color: {
              type: "string",
              description: "New icon color as HSL string, e.g. 'hsl(0 80% 50%)' for red.",
            },
            text_color: {
              type: "string",
              description: "New label text color as HSL string.",
            },
            outline_color: {
              type: "string",
              description: "Outline/border color. Set show_outline to true to display.",
            },
            show_outline: {
              type: "boolean",
              description: "Whether to show an outline ring around the location.",
            },
            subtitle: {
              type: "string",
              description: "Short subtitle to display under the location name (e.g. faction name, status).",
            },
            subtitle_color: {
              type: "string",
              description: "Color for the subtitle text.",
            },
            show_subtitle: {
              type: "boolean",
              description: "Whether to show the subtitle.",
            },
            reason: {
              type: "string",
              description: "Narrative reason for the visual change (logged for continuity).",
            },
          },
          required: ["location_name"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "query_nearby_locations",
        description: "Get locations reachable from a given location (or the player's current location if omitted). Returns connected locations with route type, distance, and travel time. Use to enrich narration with spatial context — mention nearby landmarks, warn about distant journeys, or suggest destinations.",
        parameters: {
          type: "object",
          properties: {
            from_location: { type: "string", description: "Location to query from. Defaults to player's current location if omitted." },
            max_hops: { type: "number", description: "Maximum route hops to search (1 = direct connections only, 2 = one stop away). Default 1." },
          },
        },
      },
    },
    {
      type: "function",
      function: {
        name: "get_travel_distance",
        description: "Get the distance and route information between two locations. Returns direct route details if connected, or the shortest path with total hop count if not directly connected. Use for realistic travel narration — 'It's a 3-day ride to the capital' or 'The nearest port is two stops away via the King's Road.'",
        parameters: {
          type: "object",
          properties: {
            from: { type: "string", description: "Origin location name" },
            to: { type: "string", description: "Destination location name" },
          },
          required: ["from", "to"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "query_map_locations",
        description: "List all known map locations with their coordinates, entity links, and relationships. Use to get a full spatial overview for planning travel sequences, describing the lay of the land, or checking what locations exist.",
        parameters: {
          type: "object",
          properties: {
            filter_type: { type: "string", description: "Filter by node type: 'point', 'area', 'polygon', or omit for all." },
          },
        },
      },
    },
  ],

  widgets: [
    {
      id: "world_map_panel",
      type: "sidebar_tab",
      label: "World Map",
      icon: "Map",
      component: "WorldMapWidget",
      dataBindings: {
        currentLocation: "state.current_location",
        visitedRegions: "state.visited_regions",
        revealedLocations: "state.revealed_map_nodes",
      },
      visibility: "always",
      priority: 15,
    },
    {
      id: "location_status",
      type: "status_bar_item",
      label: "Location",
      icon: "MapPin",
      component: "LocationStatusWidget",
      dataBindings: {
        currentLocation: "state.current_location",
      },
      visibility: "when_data",
      priority: 5,
    },
  ],

  initializeState(config) {
    return {
      revealed_map_nodes: [],
      ...(config.fog_of_war ? { fog_of_war_enabled: true } : {}),
    };
  },

  onEvent(event: MechanicEvent, context: MechanicContext): EventReaction | null {
    // React to region_movement events
    if (event.type === "region_enter" && event.data.region) {
      const revealed = [...(context.state.revealed_map_nodes || [])];
      if (!revealed.includes(event.data.region)) {
        revealed.push(event.data.region);
        return {
          stateUpdates: { revealed_map_nodes: revealed },
          narrativeNotes: [`The location "${event.data.region}" is now visible on the map.`],
        };
      }
    }
    return null;
  },
};
